<template>
  <div class="py-8 px-12 max-w-7xl mx-auto">
    <div class="flex items-center mb-6 space-x-4">
      <router-link to="/" class="text-brand-600 hover:text-brand-800">Back to jobs</router-link>
      <span>|</span>
      <router-link :to="`/view/${jobData.id}`" class="text-brand-600 hover:text-brand-800">View Job</router-link>
    </div>
    
    <h1 class="text-3xl font-bold mb-8">Edit Job</h1>
    
    <form @submit.prevent="saveChanges" class="space-y-6">
      <div class="flex flex-col md:flex-row md:space-x-8">
        <!-- Left side: Job fields -->
        <div class="w-3/4 md:w-3/4 space-y-6">
          <div>
            <label for="jobTitle" class="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input type="text" id="jobTitle" v-model="jobData.title" required
                   class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-600">
          </div>
          
          <div>
            <label for="company" class="block text-sm font-medium text-gray-700 mb-1">Company</label>
            <input type="text" id="company" v-model="jobData.company" required
                   class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-600">
          </div>
          
          <div>
            <label for="jobUrl" class="block text-sm font-medium text-gray-700 mb-1">Job URL</label>
            <input type="url" id="jobUrl" v-model="jobData.job_url"
                   class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-600">
          </div>
          
          <div class="flex space-x-4">
            <div class="w-1/2">
              <label for="salaryRange" class="block text-sm font-medium text-gray-700 mb-1">Salary Range</label>
              <input type="text" id="salaryRange" v-model="jobData.salary_range"
                     class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-600">
            </div>
            <div class="w-1/2">
              <label for="applicationDate" class="block text-sm font-medium text-gray-700 mb-1">Application Date</label>
              <input type="date" id="applicationDate" v-model="jobData.application_date"
                     class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-600">
            </div>
          </div>
          
          <div>
            <label for="description" class="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea id="description" v-model="jobData.description" rows="10"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-600"></textarea>
          </div>
        </div>
        
        <!-- Right side: Process steps -->
        <div class="w-[450px] mt-6 md:mt-0">
          <h2 class="text-xl font-semibold mb-3">Process Steps</h2>
          <AddStatus
            v-if="jobData.latest_process_type !== 'Rejected'"
            :current-status="jobData.status"
            :statuses="statuses"
            :status-date="statusDate"
            @update-status="updateStatus"
          />
          <ul class="space-y-4">
            <li v-for="step in jobData.process_steps" :key="step.id" class="flex items-center space-x-4">
              <input type="date" v-model="step.process_date"
                     class="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-600">
              <span>{{ step.process_type }}</span>
              <button @click="removeProcessStep(step.id)" type="button" class="text-red-600 hover:text-red-800">Remove</button>
            </li>
          </ul>
        </div>
      </div>
      
      <div class="flex space-x-4">
        <BaseButton type="submit" text="Update"/>
        <cancel-return text="Cancel" @click="cancel"/>
      </div>
    </form>
  </div>
</template>
<script lang="ts">
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import api from "@/services/api";
import type { UserJob } from '@/types';
import BaseButton from '@/components/BaseButton.vue';
import AddStatus from '@/components/AddStatus.vue';
import CancelReturn from '../components/CancelReturn.vue';
import { toLocalDateInputValue } from '@/utils/date';

export default {
  components: {
    BaseButton,
    AddStatus,
    CancelReturn
  },
  setup() {
    const route = useRoute();
    const jobData = ref<UserJob>({} as UserJob);
    const router = useRouter();
    const statuses = ref<string[]>([]);
    const statusDate = ref<string>(toLocalDateInputValue());

    const fetchJobDetails = async () => {
      const response = await api.fetchJobDetails(route.params.id as string);
      jobData.value = response.data;
      if (!jobData.value.process_steps) {
        jobData.value.process_steps = [];
      }
    };

    const fetchStatuses = async () => {
      const response = await api.fetchStatuses();
      statuses.value = response.data.statuses;
    };

    const saveChanges = async () => {
      try {
        await api.updateJob(route.params.id as string, jobData.value);
        router.push('/');
      } catch (error) {
        console.error('Error updating job:', error);
        // Handle error (e.g., show error message to user)
      }
    };

    const removeProcessStep = async (stepId: number) => {
      try {
        await api.removeProcessStep(stepId);
        jobData.value.process_steps = jobData.value.process_steps.filter(step => step.id !== stepId);
        // Update your local state or refetch job details
      } catch (error) {
        console.error('Error removing process step:', error);
        // Handle the error (e.g., show an error message to the user)
      }
    };
    const updateStatus = async (status: string, date?: string) => {
      await api.updateJobStatus(route.params.id as string, status, date);
      await fetchJobDetails();
    };

    onMounted(() => {
      fetchJobDetails();
      fetchStatuses();
    });

    return {
      jobData,
      statuses,
      statusDate,
      saveChanges,
      cancel: () => router.push('/'),
      removeProcessStep,
      updateStatus
    };
  }
};
</script>

<style scoped>
/* .grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
}

h1 {
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 1rem;
}

h2 {
  font-size: 1.25rem;
  font-weight: bold;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
}

select {
  margin-bottom: 1rem;
}

ul {
  list-style-type: none;
  padding-left: 0;
}

li {
  margin-bottom: 0.5rem;
} */
</style>
